/**
 * EmailVerificationBanner.tsx
 * 邮箱验证提示横幅
 */

import { useState } from 'react';
import { useAuthStore } from '@/stores/auth';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '@/services/api';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/useToast';
import { AlertTriangle } from 'lucide-react';
import { translate as t, useI18n } from '@/i18n';

export function EmailVerificationBanner() {
  useI18n();
  const { user } = useAuthStore();
  const { toast } = useToast();
  const [closed, setClosed] = useState(false);

  const resendMutation = useMutation({
    mutationFn: () => authApi.resendVerification({ email: user?.email || '' }),
    onSuccess: () => {
      toast({
        title: t("验证邮件已发送"),
        description: t("请查收邮件并点击验证链接"),
      });
    },
    onError: (error: any) => {
      toast({
        title: t("发送失败"),
        description: error.response?.data?.error?.message || t("请稍后重试"),
        variant: 'destructive',
      });
    },
  });

  if (!user || user.emailVerified || closed) {
    return null;
  }

  return (
    <div className="bg-amber-500/10 border-b border-amber-500/20 px-4 py-3">
      <div className="flex items-center justify-between max-w-7xl mx-auto gap-4">
        <div className="flex items-center gap-3 flex-1">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
            <span className="text-sm font-medium text-amber-900">{t("邮箱未验证")}</span>
            <span className="text-xs text-amber-700">{t("部分功能受限，请验证邮箱以解锁全部功能")}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => resendMutation.mutate()}
            disabled={resendMutation.isPending}
            className="border-amber-600 text-amber-900 hover:bg-amber-600/20"
          >
            {resendMutation.isPending ? t("发送中...") : t("重发验证邮件")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setClosed(true)}
            className="text-amber-700 hover:text-amber-900"
          >
            {t("关闭")}</Button>
        </div>
      </div>
    </div>
  );
}
